import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (v) => Number(v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });

const PAYOUT_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved for Payment' },
  { value: 'not_applicable', label: 'Not Applicable' },
  { value: 'paid', label: 'Paid' },
];

const PAYOUT_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  eligible: 'bg-amber-100 text-amber-700',
  approved: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  not_applicable: 'bg-slate-100 text-slate-500',
  cancelled: 'bg-red-100 text-red-700',
};

const PAYOUT_LABELS = {
  pending: 'Pending',
  eligible: 'Pending',
  approved: 'Approved',
  paid: 'Paid',
  not_applicable: 'N/A',
  cancelled: 'Denied',
};

// v3.15-1006: Funded classification badge — surfaces WHY a row is in its
// current state so reps and admins don't have to reverse-engineer SSOT
// status from stage/VPD/settled fields. Backend supplies the
// `funded_classification` enum on every /api/payroll row.
const FUNDED_CLASSIFICATION_META = {
  ssot_funded:        { label: 'Settled',         color: 'bg-emerald-100 text-emerald-700', tip: 'all_parties_settled = true — payable per SSOT' },
  legacy_only_review: { label: 'Needs Settlement', color: 'bg-amber-100 text-amber-800',    tip: 'Vendor payment recorded but All Parties Settled is not toggled — operator must confirm before payout' },
  pre_funding:        { label: 'Pre-Funding',     color: 'bg-slate-100 text-slate-600',     tip: 'Deal not yet funded — typical for Application Entry rows that pay regardless of funding' },
  orphan_no_client:   { label: 'Data Repair',     color: 'bg-rose-100 text-rose-700',       tip: 'No matching client doc — deal may live only in deals_new or be orphaned' },
  ineligible:         { label: 'Ineligible',      color: 'bg-zinc-100 text-zinc-500',       tip: 'Deal flagged declined / cancelled / did_not_finance' },
};

const PayrollPage = () => {
  const { isAdmin, isInternal } = useAuth();
  const navigate = useNavigate();

  const [ptcReps, setPtcReps] = useState([]);
  const [selectedRep, setSelectedRep] = useState('');
  const [commissions, setCommissions] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  // v3.15-975 PART 6: Filters. Date defaults to "this_month" so payroll
  // opens current-month-first and never blasts every historical row at once.
  const [dateRange, setDateRange] = useState('this_month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dealTypeFilter, setDealTypeFilter] = useState('all');

  // Split modal
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitTarget, setSplitTarget] = useState(null);
  const [splitSecondaryRep, setSplitSecondaryRep] = useState('');
  const [splitPrimaryPct, setSplitPrimaryPct] = useState(50);
  const [splitLoading, setSplitLoading] = useState(false);

  // Paid note modal
  const [paidNoteOpen, setPaidNoteOpen] = useState(false);
  const [paidNoteTarget, setPaidNoteTarget] = useState(null);
  const [paidNote, setPaidNote] = useState('');

  // Load reps list
  useEffect(() => {
    if (isAdmin()) {
      api.get('/payroll/ptc-reps').then(r => setPtcReps(r.data || [])).catch(() => {});
    }
  }, [isAdmin]);

  // v3.15-975 PART 6: Compute the active start/end ISO dates from the
  // dateRange selector. "this_month" is the page default; "ytd" is kept
  // as a secondary metric (and the data still renders YTD numbers in the
  // small footer card).
  const activeRange = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const pad = (n) => String(n).padStart(2, '0');
    const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    if (dateRange === 'this_month') {
      return { start: iso(new Date(y, m, 1)), end: iso(new Date(y, m + 1, 0)) };
    }
    if (dateRange === 'last_month') {
      return { start: iso(new Date(y, m - 1, 1)), end: iso(new Date(y, m, 0)) };
    }
    if (dateRange === 'ytd') {
      return { start: iso(new Date(y, 0, 1)), end: iso(now) };
    }
    if (dateRange === 'custom') {
      return { start: customStart || iso(new Date(y, m, 1)), end: customEnd || iso(now) };
    }
    return { start: '', end: '' };
  }, [dateRange, customStart, customEnd]);

  // Load data ONLY when rep is selected
  const fetchData = useCallback(async () => {
    if (!selectedRep) return;
    setLoading(true);
    try {
      const params = { user_id: selectedRep };
      if (search.trim()) params.search = search.trim();
      // v3.15-1218 PAY-MOD-002 — Migrated onto the V1 authoritative pipeline.
      // Reads are now sourced from `payroll_transactions` + `compensation_ledger`
      // + `compensation_lifecycle_events` via the /legacy-reader adapter.
      // `date_range` is a single param the adapter understands (this_month /
      // last_month / ytd / all) — sub-range logic stays server-side.
      const v1Params = {
        user_id: selectedRep,
        date_range: dateRange === 'custom' ? 'all' : (dateRange || 'this_month'),
      };
      const [commRes, sumRes] = await Promise.all([
        api.get('/legacy-reader/payroll',         { params: v1Params }),
        api.get('/legacy-reader/payroll/summary', { params: v1Params }),
      ]);
      let items = commRes.data?.items || [];
      // Client-side status + search filters retained for parity.
      if (statusFilter !== 'all') {
        items = items.filter((c) => (c.status || '').toLowerCase() === statusFilter.toLowerCase());
      }
      setCommissions(items);
      // Reshape summary into legacy keys the UI already renders.
      const t = sumRes.data?.totals || {};
      const c = sumRes.data?.counts || {};
      setSummary({
        total: sumRes.data?.grand_total || 0,
        pending: t.pending || 0,
        approved: t.approved || 0,
        paid: t.paid || 0,
        added_to_money_manager: t.added_to_money_manager || 0,
        pending_count: c.pending || 0,
        approved_count: c.approved || 0,
        paid_count: c.paid || 0,
        data_source: sumRes.data?.data_source || 'v1_authoritative',
      });
    } catch {
      toast.error('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  }, [selectedRep, search, dateRange, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Status change — v3.15-1217 PAY-MOD-001: retired. Kept as a no-op that surfaces the retirement message.
  const handleStatusChange = async (_commId, _newStatus, _notes) => {
    toast.error('This workflow has been retired (PAY-MOD-001). Use Financial Lifecycle to record stage transitions.');
  };

  // Mark paid with note
  const confirmMarkPaid = () => {
    handleStatusChange();
    setPaidNoteOpen(false);
    setPaidNoteTarget(null);
    setPaidNote('');
  };

  // Split — v3.15-1217 PAY-MOD-001: retired.
  const handleSplit = async () => {
    toast.error('Commission split has been retired (PAY-MOD-001). Configure splits via the Compensation Plan.');
    setSplitOpen(false);
  };

  if (!isInternal()) {
    return <div className="flex items-center justify-center h-64"><p className="text-slate-500">PTC users only</p></div>;
  }

  const repName = ptcReps.find(r => r.id === selectedRep)?.full_name || '';

  // Filter commissions by search (client-side) + deal type
  let filtered = search.trim()
    ? commissions.filter(c => {
        const s = search.toLowerCase();
        return (c.client_name || '').toLowerCase().includes(s)
          || (c.client_phone || '').toLowerCase().includes(s)
          || (c.client_id || '').toLowerCase().includes(s);
      })
    : commissions;
  if (dealTypeFilter !== 'all') {
    filtered = filtered.filter(c => (c.deal_type || '').toLowerCase() === dealTypeFilter.toLowerCase());
  }

  return (
    <div className="space-y-3" data-testid="payroll-page">
      {/* Header */}
      <h1 className="font-heading text-2xl font-bold text-primary" data-testid="payroll-title">Rep Payroll</h1>

      {/* v3.15-1218 PAY-MOD-002 — Migrated onto the V1 pipeline.
          This page now reads from payroll_transactions + compensation_ledger
          + compensation_lifecycle_events. Legacy mutations remain retired
          from PAY-MOD-001 (v3.15-1217). */}
      <div
        className="rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 flex items-start gap-2"
        data-testid="payroll-v1-source-banner"
      >
        <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="font-semibold flex items-center gap-2">
            V1 Authoritative Pipeline
            <span className="text-[9px] uppercase tracking-wide bg-emerald-200 text-emerald-800 px-1.5 py-0.5 rounded-full">
              live
            </span>
          </div>
          <div className="mt-0.5 leading-relaxed">
            Rows are sourced from <code>payroll_transactions</code> +{' '}
            <code>compensation_ledger</code> + <code>compensation_lifecycle_events</code>{' '}
            (PAY-MOD-002 · v3.15-1218). Legacy mutations remain retired.
          </div>
        </div>
      </div>

      {/* Select Rep + Search */}
      <div className="flex items-center gap-2 border-b pb-2 flex-wrap" data-testid="payroll-filters">
        <Select value={selectedRep} onValueChange={v => { setSelectedRep(v); setSearch(''); }}>
          <SelectTrigger className="w-[220px] h-8 text-xs" data-testid="rep-filter">
            <SelectValue placeholder="Select Rep..." />
          </SelectTrigger>
          <SelectContent>
            {ptcReps.map(r => (
              <SelectItem key={r.id} value={r.id}>{r.full_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedRep && (
          <>
            <div className="relative">
              <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-slate-400" />
              <Input
                className="h-8 text-xs pl-7 w-[220px]"
                placeholder="Search client, phone, or ID..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                data-testid="payroll-search"
              />
            </div>
            {/* v3.15-975 PART 6: Date range, Status, Deal Type filters. */}
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="payroll-date-range-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="this_month" className="text-xs">This Month</SelectItem>
                <SelectItem value="last_month" className="text-xs">Last Month</SelectItem>
                <SelectItem value="ytd" className="text-xs">YTD</SelectItem>
                <SelectItem value="custom" className="text-xs">Custom Range</SelectItem>
                <SelectItem value="all" className="text-xs">All Time</SelectItem>
              </SelectContent>
            </Select>
            {dateRange === 'custom' && (
              <>
                <Input type="date" className="h-8 text-xs w-[130px]" value={customStart} onChange={e => setCustomStart(e.target.value)} data-testid="payroll-custom-start" />
                <Input type="date" className="h-8 text-xs w-[130px]" value={customEnd} onChange={e => setCustomEnd(e.target.value)} data-testid="payroll-custom-end" />
              </>
            )}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="payroll-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All Statuses</SelectItem>
                <SelectItem value="pending" className="text-xs">Pending</SelectItem>
                <SelectItem value="approved" className="text-xs">Approved</SelectItem>
                <SelectItem value="paid" className="text-xs">Paid</SelectItem>
                <SelectItem value="not_applicable" className="text-xs">N/A</SelectItem>
              </SelectContent>
            </Select>
            <Select value={dealTypeFilter} onValueChange={setDealTypeFilter}>
              <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="payroll-deal-type-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All Types</SelectItem>
                <SelectItem value="EQF" className="text-xs">EQF</SelectItem>
                <SelectItem value="Solar Exit" className="text-xs">Solar Exit</SelectItem>
                <SelectItem value="Timeshare Exit" className="text-xs">Timeshare Exit</SelectItem>
                <SelectItem value="PTC" className="text-xs">PTC</SelectItem>
              </SelectContent>
            </Select>
          </>
        )}
      </div>

      {/* No rep selected — empty state */}
      {!selectedRep && (
        <div className="text-center py-24 text-slate-400" data-testid="payroll-empty">
          <p className="text-sm">Select a rep to view their payroll</p>
        </div>
      )}

      {/* Rep selected — show their payroll */}
      {selectedRep && (
        <>
          {/* Rep name */}
          {repName && (
            <div className="text-sm font-semibold text-slate-700" data-testid="rep-name-header">{repName}</div>
          )}

          {/* v3.15-975 PART 5: Summary cards.
              Primary row = current-month-first metrics: Total Deals,
              MTD Earnings, Pending, Approved, Paid. YTD is kept as a
              smaller secondary pill below so it doesn't dominate. */}
          <div className="grid grid-cols-5 gap-0 border rounded overflow-hidden text-center" data-testid="rep-summary">
            <div className="px-2 py-2 bg-slate-50 border-r">
              <span className="text-[10px] text-slate-500 uppercase block">Total Deals (This Month)</span>
              <span className="text-base font-bold text-slate-800" data-testid="summary-total-deals">{filtered.length}</span>
            </div>
            <div className="px-2 py-2 bg-slate-50 border-r">
              <span className="text-[10px] text-slate-500 uppercase block">MTD Earnings</span>
              <span className="text-base font-bold text-slate-800" data-testid="summary-mtd">{fmt(summary.mtd_earnings)}</span>
            </div>
            <div className="px-2 py-2 bg-amber-50 border-r">
              <span className="text-[10px] text-amber-600 uppercase font-semibold block">Pending</span>
              <span className="text-base font-bold text-amber-700" data-testid="summary-pending">{fmt(summary.pending_amount)}</span>
            </div>
            <div className="px-2 py-2 bg-blue-50 border-r">
              <span className="text-[10px] text-blue-600 uppercase font-semibold block">Approved</span>
              <span className="text-base font-bold text-blue-700" data-testid="summary-approved">{fmt(summary.approved_amount || summary.scheduled_amount)}</span>
            </div>
            <div className="px-2 py-2 bg-green-50">
              <span className="text-[10px] text-green-600 uppercase font-semibold block">Paid</span>
              <span className="text-base font-bold text-green-700" data-testid="summary-paid">{fmt(summary.paid_amount)}</span>
            </div>
          </div>
          {/* Secondary YTD pill — keeps the metric available without dominating the UI. */}
          <div className="flex justify-end items-center gap-2 -mt-1" data-testid="ytd-secondary">
            <span className="text-[10px] text-slate-400 uppercase">YTD</span>
            <span className="text-xs font-mono text-slate-600">{fmt(summary.ytd_earnings)}</span>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-slate-400 text-sm" data-testid="payroll-no-data">
              No commissions found for this rep
            </div>
          ) : (
            <div className="border rounded overflow-x-auto" data-testid="payroll-table">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-100">
                    {/* v3.15-975 PART 5: Default columns trimmed.
                        Phone + Commission Type moved out per spec — keep the
                        view focused. They can re-surface in an expandable
                        row / detail view if needed later. */}
                    <TableHead className="text-[11px] font-semibold whitespace-nowrap">Client Name</TableHead>
                    <TableHead className="text-[11px] font-semibold whitespace-nowrap">Deal Type</TableHead>
                    <TableHead className="text-[11px] font-semibold text-right whitespace-nowrap">Deal Amount</TableHead>
                    <TableHead className="text-[11px] font-semibold text-right whitespace-nowrap">Commission</TableHead>
                    <TableHead className="text-[11px] font-semibold text-center whitespace-nowrap">Payout Status</TableHead>
                    <TableHead className="text-[11px] font-semibold whitespace-nowrap">Date</TableHead>
                    {isAdmin() && <TableHead className="text-[11px] font-semibold whitespace-nowrap">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(c => {
                    const dealType = c.deal_type || (c.split_type === 'EQF' ? 'EQF' : 'PTC');
                    const displayStatus = c.status === 'eligible' ? 'pending' : c.status;

                    return (
                      <TableRow key={c.id} className={c.status === 'paid' ? 'bg-green-50/30' : ''} data-testid={`payroll-row-${c.id}`}>
                        <TableCell className="py-1.5 px-3">
                          <div className="flex items-center gap-1.5">
                            <span
                              className="text-xs font-medium cursor-pointer hover:text-blue-600 hover:underline"
                              onClick={() => c.client_id && navigate(`/clients/${c.client_id}`)}
                              data-testid={`payroll-client-${c.id}`}
                            >
                              {c.client_name || 'Unknown'}
                            </span>
                            {c.is_split && <span className="text-[9px] text-purple-500 ml-1">(split)</span>}
                            {/* v3.15-1006: SSOT funded classification badge */}
                            {c.funded_classification && FUNDED_CLASSIFICATION_META[c.funded_classification] && (
                              <span
                                className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${FUNDED_CLASSIFICATION_META[c.funded_classification].color}`}
                                title={FUNDED_CLASSIFICATION_META[c.funded_classification].tip}
                                data-testid={`payroll-funded-class-${c.id}`}
                              >
                                {FUNDED_CLASSIFICATION_META[c.funded_classification].label}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="py-1.5">
                          <Badge variant="outline" className={`text-[9px] ${
                            dealType === 'EQF' ? 'border-orange-300 text-orange-600'
                            : dealType === 'Solar Exit' ? 'border-amber-300 text-amber-600'
                            : dealType === 'Timeshare Exit' ? 'border-rose-300 text-rose-600'
                            : 'border-blue-300 text-blue-600'
                          }`} data-testid={`payroll-deal-type-${c.id}`}>
                            {dealType}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-right font-mono py-1.5">{c.deal_amount ? fmt(c.deal_amount) : '-'}</TableCell>
                        <TableCell className="text-xs text-right font-mono font-bold text-emerald-700 py-1.5">{fmt(c.amount)}</TableCell>
                        <TableCell className="text-center py-1.5">
                          {isAdmin() ? (
                            <Select
                              value={displayStatus}
                              onValueChange={(val) => {
                                if (val === 'paid') {
                                  setPaidNoteTarget(c.id);
                                  setPaidNoteOpen(true);
                                } else {
                                  handleStatusChange(c.id, val);
                                }
                              }}
                              disabled={actionLoading === c.id}
                            >
                              <SelectTrigger className="h-5 w-[140px] text-[10px] border-0 bg-transparent" data-testid={`status-select-${c.id}`}>
                                <Badge className={`${PAYOUT_COLORS[displayStatus] || ''} text-[9px]`}>
                                  {PAYOUT_LABELS[displayStatus] || displayStatus}
                                </Badge>
                              </SelectTrigger>
                              <SelectContent>
                                {PAYOUT_OPTIONS.map(opt => (
                                  <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge className={`${PAYOUT_COLORS[displayStatus] || ''} text-[10px]`}>
                              {PAYOUT_LABELS[displayStatus] || displayStatus}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-[10px] text-slate-400 py-1.5">{(c.created_at || '').slice(0, 10)}</TableCell>
                        {isAdmin() && (
                          <TableCell className="py-1.5">
                            {dealType === 'PTC' && !c.is_split && c.status !== 'paid' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-5 text-[10px] text-purple-600 hover:text-purple-800 px-1"
                                onClick={() => { setSplitTarget(c); setSplitSecondaryRep(''); setSplitPrimaryPct(50); setSplitOpen(true); }}
                                data-testid={`split-btn-${c.id}`}
                              >
                                Split
                              </Button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}

      {/* Split Dialog */}
      <Dialog open={splitOpen} onOpenChange={setSplitOpen}>
        <DialogContent className="max-w-sm" data-testid="split-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">Split Commission</DialogTitle>
          </DialogHeader>
          {splitTarget && (
            <div className="space-y-3 text-xs">
              <div className="flex justify-between text-slate-500">
                <span>Client: {splitTarget.client_name}</span>
                <span>Total: {fmt(splitTarget.amount)}</span>
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Second Rep</label>
                <Select value={splitSecondaryRep} onValueChange={setSplitSecondaryRep}>
                  <SelectTrigger className="h-7 text-xs" data-testid="split-rep-select">
                    <SelectValue placeholder="Select rep..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ptcReps.filter(r => r.id !== splitTarget.user_id).map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">Primary %</label>
                  <Input type="number" className="h-7 text-xs" value={splitPrimaryPct} onChange={e => setSplitPrimaryPct(Number(e.target.value))} min={1} max={99} data-testid="split-primary-pct" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">Secondary %</label>
                  <Input type="number" className="h-7 text-xs" value={100 - splitPrimaryPct} disabled />
                </div>
              </div>
              <div className="flex justify-between text-[10px] text-slate-500 border-t pt-2">
                <span>Primary: {fmt(splitTarget.amount * (splitPrimaryPct / 100))}</span>
                <span>Secondary: {fmt(splitTarget.amount * ((100 - splitPrimaryPct) / 100))}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setSplitOpen(false)}>Cancel</Button>
            <Button size="sm" className="h-7 text-xs bg-purple-600 hover:bg-purple-700" onClick={handleSplit} disabled={!splitSecondaryRep || splitLoading} data-testid="split-confirm-btn">
              {splitLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Split Commission'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Paid Note Dialog */}
      <Dialog open={paidNoteOpen} onOpenChange={setPaidNoteOpen}>
        <DialogContent className="max-w-xs" data-testid="paid-note-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">Mark as Paid</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-[10px] text-slate-500 block">Note (optional)</label>
            <Input className="h-7 text-xs" placeholder="e.g. paid outside system" value={paidNote} onChange={e => setPaidNote(e.target.value)} data-testid="paid-note-input" />
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setPaidNoteOpen(false)}>Cancel</Button>
            <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700" onClick={confirmMarkPaid} data-testid="paid-note-confirm">Confirm Paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayrollPage;
